import React, { useState } from "react";
import moment from "moment";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";
import styled from "styled-components";
import { Card } from "./styles";

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  h3 {
    font-size: 16px;
    font-weight: 600;
    margin: 0;
  }
`;

const NavButtons = styled.div`
  display: flex;
  gap: 8px;

  button {
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    color: #4b5563;
    padding: 4px;
    border-radius: 4px;
    
    &:hover {
        background: #f3f4f6;
    }
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  text-align: center;
`;

const DayLabel = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: #6b7280;
  padding: 8px 0;
  text-transform: uppercase;
`;

const DayCell = styled.div`
  font-size: 13px;
  padding: 8px 0;
  cursor: pointer;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  color: ${(props) => (props.isSelected ? "white" : props.isCurrentMonth ? "#111827" : "#d1d5db")};
  background: ${(props) => (props.isSelected ? "#2563eb" : props.hasEntry ? "#EBF2FF" : "transparent")};
  font-weight: ${(props) => (props.isSelected || props.hasEntry ? "600" : "400")};
  border: ${(props) => (props.hasEntry && !props.isSelected ? "1px solid #4178ff33" : "none")};

  &:hover {
    background: ${(props) => (props.isSelected ? "#2563eb" : props.hasEntry ? "#EBF2FF" : "#f3f4f6")};
    opacity: 0.9;
  }
`;

const LifestyleCalendar = ({ selectedDate, onDateChange, entries = [] }) => {
  const [viewDate, setViewDate] = useState(moment(selectedDate));

  const startOfMonth = moment(viewDate).startOf("month");
  const endOfMonth = moment(viewDate).endOf("month");
  const startDate = moment(startOfMonth).startOf("week");
  const endDate = moment(endOfMonth).endOf("week");

  const calendarDays = [];
  let day = moment(startDate);

  while (day.isBefore(endDate, "day")) {
    calendarDays.push(moment(day));
    day.add(1, "day");
  }

  const handlePrevMonth = () => setViewDate(moment(viewDate).subtract(1, "month"));
  const handleNextMonth = () => setViewDate(moment(viewDate).add(1, "month"));

  // Create a set of dates that have entries for efficient lookup
  const entryDates = new Set(entries.map(e => e.date));

  return (
    <Card>
      <CalendarHeader>
        <h3>{viewDate.format("MMMM YYYY")}</h3>
        <NavButtons>
          <button onClick={handlePrevMonth}><IoChevronBack /></button>
          <button onClick={handleNextMonth}><IoChevronForward /></button>
        </NavButtons>
      </CalendarHeader>
      <CalendarGrid>
        {["S", "M", "T", "W", "T", "F", "S"].map((day) => (
          <DayLabel key={day}>{day}</DayLabel>
        ))}
        {calendarDays.map((date, index) => {
          const formattedDate = date.format("YYYY-MM-DD");
          const isSelected = date.isSame(selectedDate, "day");
          const isCurrentMonth = date.isSame(viewDate, "month");
          const hasEntry = entryDates.has(formattedDate);

          return (
            <DayCell
              key={index}
              isSelected={isSelected}
              isCurrentMonth={isCurrentMonth}
              hasEntry={hasEntry}
              onClick={() => onDateChange(date.toDate())}
            >
              {date.date()}
            </DayCell>
          );
        })}
      </CalendarGrid>
    </Card>
  );
};

export default LifestyleCalendar;
